/**
 * JML mobile sticky add-to-cart bar.
 *
 * Shows a fixed bar at the bottom of the screen (mobile only, < 750px) once the main
 * add-to-cart button has scrolled above the viewport; hides it again near the footer or when the
 * main button is visible / disabled. Clicking the bar clicks the real main button, so the selected
 * variant, quantity, cart drawer and analytics all behave exactly like the main form.
 *
 * Helio/Horizon scroll quirk: on desktop html/body are overflow:hidden and .page-wrapper scrolls,
 * so we listen on document in the capture phase and read Math.max(scrollY, .page-wrapper.scrollTop).
 */
(function () {
  if (customElements.get('jml-sticky-atc')) return;

  const MOBILE = '(max-width: 749px)';

  class JmlStickyAtc extends HTMLElement {
    connectedCallback() {
      // Render at <body> level so no transformed ancestor can break position: fixed.
      // If the product section re-renders, the fresh element replaces the previous one.
      if (this.parentElement !== document.body) {
        document.querySelectorAll('body > jml-sticky-atc').forEach((el) => {
          if (el !== this) el.remove();
        });
        document.body.appendChild(this);
        return;
      }

      this.abort = new AbortController();
      const { signal } = this.abort;
      this.raf = 0;
      this.visible = false;
      this.mq = window.matchMedia(MOBILE);
      this.bar = this.querySelector('[data-jml-bar]');
      this.priceEl = this.querySelector('[data-jml-price]');
      this.button = this.querySelector('[data-jml-add]');

      const schedule = () => {
        if (this.raf) return;
        this.raf = window.requestAnimationFrame(() => {
          this.raf = 0;
          this.update();
        });
      };

      document.addEventListener('scroll', schedule, { passive: true, capture: true, signal });
      window.addEventListener('resize', schedule, { passive: true, signal });
      // Variant changes re-render the price: re-sync shortly after any change in the section.
      document.addEventListener('change', () => window.setTimeout(() => this.sync(), 400), { signal });

      if (this.button) {
        this.button.addEventListener(
          'click',
          (event) => {
            event.preventDefault();
            const main = this.mainButton();
            if (main && !main.disabled) {
              main.click();
            } else {
              const target = this.buyButtons();
              if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          },
          { signal }
        );
      }

      if (this.bar) this.bar.inert = true;
      this.markChat();
      window.setTimeout(() => this.markChat(), 4000);
      schedule();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.raf) window.cancelAnimationFrame(this.raf);
      document.documentElement.classList.remove('jml-sticky-atc-on');
    }

    get sectionSelector() {
      return this.dataset.sectionId ? `#shopify-section-${this.dataset.sectionId}` : '';
    }

    buyButtons() {
      return document.querySelector(`${this.sectionSelector} .buy-buttons-block`.trim());
    }

    mainButton() {
      const block = this.buyButtons();
      return block ? block.querySelector('[ref="addToCartButton"]') : null;
    }

    /** Leave room for the Shopify Inbox chat button when it's on the page. */
    markChat() {
      const hasChat =
        Boolean(customElements.get('shopify-chat')) ||
        Boolean(document.querySelector('#ShopifyChat, #dummy-chat-button-iframe'));
      this.classList.toggle('jml-sticky-atc--chat', hasChat);
    }

    /** Copy the live price shown in the main product section. */
    sync() {
      if (!this.priceEl) return;
      const source = document.querySelector(`${this.sectionSelector} product-price .price`.trim());
      if (!source) return;
      const clone = source.cloneNode(true);
      clone.querySelectorAll('.visually-hidden').forEach((el) => el.remove());
      const text = clone.textContent.replace(/\s+/g, ' ').trim();
      if (text) this.priceEl.textContent = text;
    }

    update() {
      const wrapper = document.querySelector('.page-wrapper');
      const y = Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0);
      const main = this.mainButton();
      let show = false;

      if (this.mq.matches && main && y > 0 && !main.disabled) {
        const rect = main.getBoundingClientRect();
        show = rect.bottom < 0;
        if (show) {
          const footer = document.querySelector('footer');
          if (footer && footer.getBoundingClientRect().top < window.innerHeight) show = false;
        }
      }

      if (show === this.visible) return;
      this.visible = show;
      if (show) this.sync();
      if (this.bar) this.bar.dataset.visible = String(show);
      if (this.bar) this.bar.inert = !show;
      document.documentElement.classList.toggle('jml-sticky-atc-on', show);
    }
  }

  customElements.define('jml-sticky-atc', JmlStickyAtc);
})();
